import { cn } from '@/lib/utils';
import { IconTile } from '../icon-tile';
import type { NodeSummaryStats } from '@/hooks/use-events-summary';

interface EventsNodeRowProps {
  node: NodeSummaryStats;
  onClick: (nodeId: string) => void;
}

interface StatBadgeProps {
  label: string;
  variant?: 'error' | 'warning' | 'default';
}

const badgeVariantStyles: Record<string, string> = {
  error:
    'bg-rose-100 text-rose-900 border border-rose-500 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-700',
  warning:
    'bg-yellow-100 text-yellow-900 border border-yellow-500 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-700',
  default: 'bg-muted text-foreground',
};

function StatBadge({ label, variant = 'default' }: StatBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-md shadow-xs',
        badgeVariantStyles[variant],
      )}
    >
      {label}
    </span>
  );
}

export function EventsNodeRow({ node, onClick }: EventsNodeRowProps) {
  const badges: { label: string; variant: StatBadgeProps['variant'] }[] = [];

  if (node.errors > 0) {
    badges.push({
      label: `${node.errors} Error${node.errors > 1 ? 's' : ''}`,
      variant: 'error',
    });
  }
  if (node.retries > 0) {
    badges.push({
      label: `${node.retries} Retr${node.retries > 1 ? 'ies' : 'y'}`,
      variant: 'warning',
    });
  }
  if (node.events > 0) {
    badges.push({
      label: `${node.events} Event${node.events > 1 ? 's' : ''}`,
      variant: 'default',
    });
  }
  if (node.toolCalls > 0) {
    badges.push({
      label: `${node.toolCalls} Tool call${node.toolCalls > 1 ? 's' : ''}`,
      variant: 'default',
    });
  }
  if (node.thoughts > 0) {
    badges.push({
      label: `${node.thoughts} Thought${node.thoughts > 1 ? 's' : ''}`,
      variant: 'default',
    });
  }

  const runningLabel = node.statusText.replace(/\.{3}$/, '');

  return (
    <button
      type='button'
      className='flex flex-col gap-2 w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer'
      onClick={() => onClick(node.nodeId)}
    >
      <div data-entry-label className='flex items-center gap-2 min-w-0'>
        {node.icon && (
          <div
            className={cn(
              'relative shrink-0 rounded-lg',
              node.isRunning &&
                'overflow-hidden ring-2 ring-primary/55 ring-offset-0',
            )}
          >
            <IconTile
              icon={node.icon}
              backgroundColor={node.iconColor || ''}
              iconColor={node.iconColor ? '#ffffff' : ''}
              size='small'
              style={
                !node.useThemeColors && node.iconColor
                  ? {
                      background: `linear-gradient(to bottom, ${node.iconColor}, color-mix(in srgb, ${node.iconColor} 85%, black))`,
                    }
                  : undefined
              }
              className={
                node.useThemeColors
                  ? 'bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
                  : ''
              }
              iconClassName={
                node.useThemeColors
                  ? 'size-4 text-white dark:text-slate-950'
                  : 'size-4'
              }
            />
            {node.isRunning && (
              <span
                className='animate-run-sheen pointer-events-none absolute inset-y-0 left-0 w-[55%]'
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                }}
              />
            )}
          </div>
        )}
        <div className='flex flex-col gap-0.5 min-w-0 flex-1'>
          <span className='text-sm font-medium truncate'>
            {node.displayName}
          </span>
          {node.isRunning ? (
            <span className='flex items-center gap-1.5 text-sm text-primary min-w-0'>
              <span className='truncate'>{runningLabel}</span>
              <span className='inline-flex gap-[3px] shrink-0'>
                <span className='size-1 rounded-full bg-primary animate-run-dot' />
                <span
                  className='size-1 rounded-full bg-primary animate-run-dot'
                  style={{ animationDelay: '0.16s' }}
                />
                <span
                  className='size-1 rounded-full bg-primary animate-run-dot'
                  style={{ animationDelay: '0.32s' }}
                />
              </span>
            </span>
          ) : (
            <span className='text-sm text-muted-foreground truncate'>
              {node.statusText}
            </span>
          )}
        </div>
      </div>
      {badges.length > 0 && (
        <div data-entry-card className='flex flex-wrap gap-1'>
          {badges.map((badge) => (
            <StatBadge
              key={badge.label}
              label={badge.label}
              variant={badge.variant}
            />
          ))}
        </div>
      )}
    </button>
  );
}
